import AddressForm from "../AddressForm";
import { updateCart } from "./cart";

const DOMESTIC_COUNTRIES = ["US", "CA"];

// The legacy AddressForm, unchanged except for what happens after a save:
// instead of following the redirect, collapse the section and refresh the
// page regions in place. Error handling and the FedEx recommendation modal
// ("addressError" / "saveAddress" events) are inherited as-is.
export default () => ({
    ...AddressForm(),

    // Organization is hidden for non-business addresses (see
    // _checkout-single.scss), so don't let a stale value submit with it.
    clearOrganization(event) {
        const select = event.target;
        if (
            !select.matches(".isBusinessAddress select") ||
            select.value !== "0"
        ) {
            return;
        }
        const organization = select
            .closest(".js-address-fields")
            ?.querySelector(".organization input");
        if (organization) {
            organization.value = "";
        }
    },

    async save() {
        // Swapped-out regions leave their window "saveAddress" listener behind.
        // The Continue button is only aria-disabled while busy, so it can
        // still be pressed.
        if (!this.form.isConnected || this.busy || this.addressSaving) {
            return;
        }

        try {
            document.querySelectorAll("div.flash-error").forEach((el) => {
                el.remove();
            });
            this.form.querySelectorAll("div.error").forEach((el) => {
                el.innerHTML = "";
            });
            this.form
                .querySelectorAll("[aria-invalid]")
                .forEach((el) => el.removeAttribute("aria-invalid"));
            this.errors = null;

            const form = this.form;
            if (form.reportValidity) {
                form.reportValidity();
            }
            if (form.checkValidity && form.checkValidity() == false) {
                return;
            }

            this.addressSaving = true;
            this.$dispatch("addressSaving", { addressSaving: true });

            const data = new FormData(form);

            // International billing can only pay with PayPal (gateway 2).
            const billingCountryCode = data.get("billingAddress[countryCode]");
            if (
                billingCountryCode &&
                !DOMESTIC_COUNTRIES.includes(billingCountryCode)
            ) {
                data.append("gatewayId", 2);
            }

            // `whileBusy` comes from the parent SinglePageCheckout scope.
            await this.whileBusy(async () => {
                await updateCart(data);
                await this.refresh("delivery");
            });
            this.advance("delivery");
        } catch (e) {
            console.error(e);

            if (e.response && e.response.data) {
                if (e.response.data.message) {
                    this.addFlashError(e.response.data.message);
                }
                const errors = e.response.data.errors;
                this.addFormErrors(errors);
                this.markInvalidFields();
                this.$dispatch("addressError", { errors });
                return;
            }

            this.addFlashError("Couldn't save address.");
        } finally {
            this.addressSaving = false;
            this.$dispatch("addressSaving", { addressSaving: false });
        }
    },

    // Tie each field error to its input (aria-invalid + aria-describedby) and
    // move focus to the first one, so screen readers hear what needs fixing.
    markInvalidFields() {
        let first = null;
        this.form.querySelectorAll("div.error").forEach((errorDiv, index) => {
            const field = errorDiv.parentElement.querySelector(
                "input:not([type=hidden]), select, textarea",
            );
            if (!field || !errorDiv.textContent.trim()) {
                return;
            }
            errorDiv.id ||= `spc-address-error-${index}`;
            field.setAttribute("aria-invalid", "true");
            field.setAttribute("aria-describedby", errorDiv.id);
            first ??= field;
        });
        first?.focus();
    },

    // Legacy puts the error above the site header, which on one long page is
    // off-screen. Show it at the top of this form instead.
    addFlashError(message) {
        const errorDiv = document.createElement("div");
        errorDiv.classList.add("flash-error", "spc-flash");
        errorDiv.setAttribute("role", "alert");
        errorDiv.innerHTML = "<div><strong>Error</strong></div>";
        errorDiv.firstChild.append(message);

        this.form.prepend(errorDiv);
        errorDiv.scrollIntoView({ behavior: "smooth", block: "center" });
    },
});
